import { useContext, useMemo } from 'react';
import { useSnackbar } from '@fluentsms/snackbar';
import { EventContextType } from '../providers';
import { SubscriptionCallback, SubscriptionChannelEventSchema, SubscriptionOptions } from '../types';

/**
 * Hook to subscribe to an event where you control the lifecycle of the event subscription.
 * @returns subscribe function, calling subscribe returns a function to unsubscribe from the event.
 */
export function useEventSubscription(context: React.Context<EventContextType>) {
  const { subscribe } = useContext<EventContextType>(context);
  const snackbar = useSnackbar();

  const asyncSubscribe = useMemo(
    () =>
      function <T extends SubscriptionChannelEventSchema>(
        options: SubscriptionOptions<T>,
        onSuccess: SubscriptionCallback<T>,
        onError?: (result: Error) => void,
        onComplete?: () => void,
      ) {
        return subscribe<T>(options, (data) => {
          try {
            if (!data) {
              throw new Error(`No data received for ${options.schema._def.description ?? 'event'}.`);
            } else if (data.errors?.length) {
              throw new Error(data.errors.join(','));
            } else {
              onSuccess(data);
            }
          } catch (err) {
            if (onError) {
              onError?.(err);
            } else {
              snackbar.enqueueSnackbar(err.message, { variant: 'error' });
            }
          } finally {
            onComplete?.();
          }
        });
      },
    [subscribe, snackbar],
  );

  return { subscribe: asyncSubscribe };
}

export default useEventSubscription;
